/**
 * B614 — the SHOW face's poster wall (553:2 @1440, 555:130 @390), pure half.
 * The switch is the tenant's type (SHOW); every other type keeps today's
 * tiles. What feeds what (the frame's NOTE 555:249): the nights are the
 * product's ON_SALE slots in the next 14 days grouped by the night they fall
 * on, a chip per slot whose state comes from slot.availability; the badge is
 * B615's age (a per-showtime override says its weekday); the price foot is the
 * card's ticket types. "Few left" is the storefront's standing LOW_SEATS rule
 * (≤5 left): the frame's "under 15%" needs a capacity the public availability
 * read deliberately never carries (§11: available units ONLY).
 */
import type { PosterNight, PosterShowtime } from "@transact/ui";
import type { PoolAvailability, ProductSummary, SlotSummary } from "../ui/screens.js";
import { seatsLeft } from "./browse.js";

export const POSTER_WINDOW_DAYS = 14;
/** The storefront's one "few left" line (departures, tickets step, widget). */
export const POSTER_LOW_SEATS = 5;
const DAY_MS = 86_400_000;

interface Zone {
  readonly locale?: string;
  readonly timeZone?: string;
}

const parts = (ms: number, z: Zone, o: Intl.DateTimeFormatOptions): Record<string, string> =>
  Object.fromEntries(
    new Intl.DateTimeFormat(z.locale ?? "en-US", { ...o, ...(z.timeZone !== undefined ? { timeZone: z.timeZone } : {}) })
      .formatToParts(ms)
      .map((p) => [p.type, p.value]),
  );

/** The night a slot falls on, as a sortable key ("2026-10-02"). */
function nightKey(ms: number, z: Zone): string {
  const p = parts(ms, z, { year: "numeric", month: "2-digit", day: "2-digit" });
  return `${p["year"]}-${p["month"]}-${p["day"]}`;
}

/** "FRI 2" — the frame's night label. */
function nightLabel(ms: number, z: Zone): string {
  const p = parts(ms, z, { weekday: "short", day: "numeric" });
  return `${p["weekday"]} ${p["day"]}`.toUpperCase();
}

/** "7:00" / "9:45" — the club's wall clock, no meridiem (a comedy night is an evening). */
export function showTime(ms: number, z: Zone = {}): string {
  const p = parts(ms, z, { hour: "numeric", minute: "2-digit", hour12: true });
  return `${p["hour"]}:${p["minute"]}`;
}

function weekdayShort(ms: number, z: Zone): string {
  return parts(ms, z, { weekday: "short" })["weekday"] ?? "";
}

export function showtimeState(pools: readonly PoolAvailability[] | undefined): PosterShowtime["state"] {
  const left = pools === undefined ? undefined : seatsLeft(pools);
  if (left === 0) return "soldout";
  if (left !== undefined && left <= POSTER_LOW_SEATS) return "few";
  return "open";
}

/**
 * The nights a product runs in the window: ON_SALE slots starting in
 * [now, now + 14 days), grouped by night, earliest first. An unread
 * availability is an OPEN chip (cart.add stays the real wall) — never a
 * guessed "few left".
 */
export function posterNights(
  slots: readonly SlotSummary[],
  availability: Readonly<Record<string, readonly PoolAvailability[]>>,
  now: number,
  z: Zone = {},
): PosterNight[] {
  const end = now + POSTER_WINDOW_DAYS * DAY_MS;
  const inWindow = slots.filter((s) => s.status === "ON_SALE" && s.start >= now && s.start < end).sort((a, b) => a.start - b.start);
  const nights = new Map<string, { day: string; showtimes: PosterShowtime[] }>();
  for (const s of inWindow) {
    const key = nightKey(s.start, z);
    const night = nights.get(key) ?? { day: nightLabel(s.start, z), showtimes: [] };
    const time = showTime(s.start, z);
    night.showtimes.push({ id: s.slotId, time, state: showtimeState(availability[s.slotId]), label: `${night.day.charAt(0)}${night.day.slice(1).toLowerCase()}, ${time}` });
    nights.set(key, night);
  }
  return [...nights.values()];
}

/** The one urgency flag: a sold-out showtime outranks a few-left one; the earliest of its kind. */
export function posterFlag(nights: readonly PosterNight[]): { readonly tone: "few" | "soldout"; readonly label: string } | undefined {
  for (const tone of ["soldout", "few"] as const) {
    for (const n of nights) {
      const s = n.showtimes.find((x) => x.state === tone);
      if (s !== undefined) return { tone, label: `${tone === "soldout" ? "SOLD OUT" : "FEW LEFT"} · ${n.day.split(" ")[0]!} ${s.time}` };
    }
  }
  return undefined;
}

/**
 * The age badge: the product's age ("21+"), then each weekday whose showtimes
 * carry their own ("21+ · Sun 18+", B615 3b) — from the slots in the window.
 */
export function posterAge(minAge: number | undefined, slots: readonly SlotSummary[], now: number, z: Zone = {}): string | undefined {
  const end = now + POSTER_WINDOW_DAYS * DAY_MS;
  const overrides = new Map<string, number>();
  for (const s of slots) {
    if (s.status !== "ON_SALE" || s.start < now || s.start >= end || s.minAge === undefined || s.minAge === minAge) continue;
    const day = weekdayShort(s.start, z);
    if (!overrides.has(day)) overrides.set(day, s.minAge);
  }
  const head = minAge !== undefined ? [`${String(minAge)}+`] : [];
  const rest = [...overrides].map(([day, age]) => `${day} ${String(age)}+`);
  const all = [...head, ...rest];
  return all.length === 0 ? undefined : all.join(" · ");
}

/** Soonest night first; a show with no night in the window keeps its catalog place after them. */
export function wallOrder<T extends { readonly productId: string }>(products: readonly T[], firstStart: ReadonlyMap<string, number>): T[] {
  return [...products]
    .map((p, i) => ({ p, i, at: firstStart.get(p.productId) ?? Number.POSITIVE_INFINITY }))
    .sort((a, b) => (a.at === b.at ? a.i - b.i : a.at - b.at))
    .map((x) => x.p);
}

/** "$35" for whole amounts, "$35.50" otherwise — the poster's figures. */
export function posterMoney(minor: number, currency: string, locale = "en-US"): string {
  const whole = minor % 100 === 0;
  return new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(minor / 100);
}

/** The price foot: "from $35" (or the one price) and "GA $35 · VIP front section $55". */
export function posterPrices(card: Pick<ProductSummary, "variants">): { readonly price?: string; readonly detail?: string } {
  const priced = card.variants.filter((v) => v.displayPrice !== undefined && v.constraints?.requiresVariantId === undefined);
  if (priced.length === 0) return {};
  const amounts = priced.map((v) => v.displayPrice!.amount);
  const currency = priced[0]!.displayPrice!.currency;
  const low = Math.min(...amounts);
  const differ = amounts.some((a) => a !== low);
  const price = differ ? `from ${posterMoney(low, currency)}` : posterMoney(low, currency);
  const detail = priced.length > 1 ? priced.map((v) => `${v.name} ${posterMoney(v.displayPrice!.amount, v.displayPrice!.currency)}`).join(" · ") : undefined;
  return { price, ...(detail !== undefined ? { detail } : {}) };
}

const COUNT_WORDS = ["", "One", "Two", "Three", "Four", "Five"];

/**
 * The wall's head: "THIS MONTH AT <club>" over the month's name, and one line
 * of house facts the posters share — how many shows most nights, the age most
 * shows hold ("21+ unless marked"), the drink minimum, a final sale — each
 * said only when a read carries it.
 */
export function wallHead(
  clubName: string | undefined,
  products: readonly Pick<ProductSummary, "presentation">[],
  nights: readonly (readonly PosterNight[])[],
  now: number,
  z: Zone = {},
): { readonly eyebrow: string; readonly title: string; readonly facts?: string } {
  const month = parts(now, z, { month: "long" })["month"] ?? "";
  const facts: string[] = [];
  const perNight = new Map<string, number>();
  for (const ns of nights) for (const n of ns) perNight.set(n.day, (perNight.get(n.day) ?? 0) + n.showtimes.length);
  const most = mode([...perNight.values()]);
  if (most !== undefined && most >= 1 && most < COUNT_WORDS.length) facts.push(`${COUNT_WORDS[most]!} show${most === 1 ? "" : "s"} most nights`);
  const policies = products.map((p) => p.presentation?.policies).filter((x) => x !== undefined);
  const age = mode(policies.map((p) => p.minAge).filter((a): a is number => a !== undefined));
  if (age !== undefined) facts.push(`${String(age)}+ unless marked`);
  const drinks = mode(policies.map((p) => p.drinkMinimum).filter((d): d is number => d !== undefined && d > 0));
  if (drinks !== undefined) facts.push(`${String(drinks)}-drink minimum in the showroom`);
  if (policies.length > 0 && policies.filter((p) => p.refundPolicy === "FINAL").length * 2 > policies.length) facts.push("all sales final");
  return {
    eyebrow: clubName !== undefined ? `This month at ${clubName}` : "This month",
    title: month,
    ...(facts.length > 0 ? { facts: facts.join(" · ") } : {}),
  };
}

function mode(xs: readonly number[]): number | undefined {
  const count = new Map<number, number>();
  for (const x of xs) count.set(x, (count.get(x) ?? 0) + 1);
  let best: number | undefined;
  for (const [x, c] of count) if (best === undefined || c > count.get(best)! || (c === count.get(best)! && x > best)) best = x;
  return best;
}
